import { PriceUtils } from '@wix/bookings-uou-mappers';
import { Divider } from '@wix/wix-base-ui';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { ExperimentsConsts } from '../../../../consts/experiments';
import { PaymentOption, PaymentType } from '../../../../types/types';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import Text, { TextType } from '../../Text/Text';
import { DiscountRuleName, PriceBeforeDiscount } from '../Discount/Discount';
import { PaymentDetailsDataHooks } from './dataHooks';
import { classes } from './PaymentDetails.st.css';

interface PayNowLaterSectionProps {
  label: string;
  price: number;
  currency: string | undefined;
  locale: string;
  dataHook: string;
  amountDataHook: string;
  discountRuleName?: string;
  priceBeforeDiscountText: string | undefined;
  isCart: boolean | undefined;
  discountCalculatedAtCheckout?: boolean;
}

const PayNowLaterSection = ({
  dataHook, amountDataHook, label,
  price, currency = 'USD', locale,
  discountRuleName, priceBeforeDiscountText,
  isCart, discountCalculatedAtCheckout,
}: PayNowLaterSectionProps) => <>
  <Divider className={classes.divider} />
  <div className={classes.payNowLaterSection} data-hook={dataHook}>
    <div className={classes.payNowLaterDetails}>
      <Text type={TextType.Secondary}>
        {label}
      </Text>
      <div className={classes.priceWrapper}>
        <PriceBeforeDiscount price={priceBeforeDiscountText} type={TextType.Secondary} />
        <Text type={TextType.Secondary} data-hook={amountDataHook}>
          {PriceUtils.getFormattedCurrency({ price, currency, locale })}
        </Text>
      </div>
    </div>
    <DiscountRuleName
      name={discountRuleName}
      isCart={isCart}
      calculatedAtCheckout={discountCalculatedAtCheckout}
      align="end"
    />
  </div>
</>;

export interface PaymentDetailsProps {
  paymentOption: PaymentOption;
  payNowAmount?: number;
  payLaterAmount?: number;
  currency?: string;
  priceBeforeDiscountText?: string;
  discountRuleName?: string;
  isCart: boolean | undefined;
  discountCalculatedAtCheckout?: boolean;
}

export const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  paymentOption,
  payNowAmount,
  payLaterAmount,
  currency,
  priceBeforeDiscountText,
  discountRuleName,
  isCart,
  discountCalculatedAtCheckout,
}) => {
  const { label, suffix } = paymentOption;
  const { t } = useTranslation();
  const { dateRegionalSettingsLocale } = useFormComponentContext();
  const { experiments } = useExperiments();

  const showPlanDetails =
    (paymentOption.type === PaymentType.BUY_PLAN_AND_BOOK ||
      paymentOption.type === PaymentType.BUY_PLAN ||
      paymentOption.type === PaymentType.USE_PLAN) &&
    !!paymentOption.pricingDetails;

  const showPayNow = !!(paymentOption.type === PaymentType.USE_PLAN && payNowAmount && payNowAmount > 0);
  const showPayLater = experiments.enabled(ExperimentsConsts.AddonsWithPlanOnlyUOU) &&
    !!(paymentOption.type === PaymentType.USE_PLAN && payLaterAmount && payLaterAmount > 0);

  return (
    <div data-hook={PaymentDetailsDataHooks.PAYMENT_DETAILS}>
      <div className={classes.paymentDetails}>
        <Text type={TextType.Secondary}>{label}</Text>
        {suffix ? (
          <Text className={classes.planSlotsLeft} type={TextType.Secondary}>
            {suffix}
          </Text>
        ) : null}
      </div>
      {showPlanDetails && (
        <Text
          className={classes.planPricingDetails}
          type={TextType.Secondary}
          data-hook={PaymentDetailsDataHooks.PLAN_DETAILS}
        >
          {paymentOption.pricingDetails}
        </Text>
      )}
      {showPayNow && <PayNowLaterSection
        dataHook={PaymentDetailsDataHooks.PAY_NOW_SECTION}
        amountDataHook={PaymentDetailsDataHooks.PAY_NOW_AMOUNT}
        label={t('app.payment.summary.pay-now.label')}
        price={payNowAmount}
        currency={currency}
        locale={dateRegionalSettingsLocale}
        discountRuleName={discountRuleName}
        priceBeforeDiscountText={priceBeforeDiscountText}
        isCart={isCart}
        discountCalculatedAtCheckout={discountCalculatedAtCheckout} />}
      {showPayLater && <PayNowLaterSection
        dataHook={PaymentDetailsDataHooks.PAY_LATER_SECTION}
        amountDataHook={PaymentDetailsDataHooks.PAY_LATER_AMOUNT}
        label={t('app.payment.summary.pay-later.label')}
        price={payLaterAmount}
        currency={currency}
        locale={dateRegionalSettingsLocale}
        discountRuleName={discountRuleName}
        priceBeforeDiscountText={priceBeforeDiscountText}
        isCart={isCart}
        discountCalculatedAtCheckout={discountCalculatedAtCheckout} />}
    </div>
  );
};
